// src/pages/Legal/TermsOfService.tsx

import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Legal.module.css';

const TOC = [
  { id: 'acceptance', label: 'Acceptance of Terms' },
  { id: 'service', label: 'Service Description' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'registration', label: 'Account Registration' },
  { id: 'third-party', label: 'Third-Party Services' },
  { id: 'risk', label: 'Trading Risk Disclaimer' },
  { id: 'ip', label: 'Intellectual Property' },
  { id: 'liability', label: 'Limitation of Liability' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'termination', label: 'Termination' },
  { id: 'modifications', label: 'Modifications' },
  { id: 'governing-law', label: 'Governing Law' },
  { id: 'contact', label: 'Contact' },
];

const TermsOfService: React.FC = () => {
  return (
    <div className={styles.legalPage} id="top">
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last updated: March 15, 2026</p>
      </header>

      {/* Table of Contents */}
      <nav className={styles.toc}>
        <p className={styles.tocTitle}>Table of Contents</p>
        <ol className={styles.tocList}>
          {TOC.map((item) => (
            <li key={item.id}>
              <a href={`#${item.id}`} className={styles.tocLink}>
                {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* 1. Acceptance */}
      <section className={styles.section} id="acceptance">
        <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
        <p>
          By accessing or using SignalDeck ("the Service"), you agree to be bound by these Terms of Service. If you do
          not agree with any part of these terms, you must not use the Service.
        </p>
        <p>
          These terms constitute a legally binding agreement between you and SignalDeck. Your continued use of the
          Service after any changes to these terms constitutes acceptance of those changes.
        </p>
      </section>

      {/* 2. Service Description */}
      <section className={styles.section} id="service">
        <h2 className={styles.sectionTitle}>2. Service Description</h2>
        <p>
          SignalDeck is an automated trading dashboard that connects to third-party brokerage services (currently Alpaca
          Markets) via OAuth. The Service provides tools for:
        </p>
        <ul>
          <li>Monitoring positions and portfolio performance in real time</li>
          <li>Executing stock and options trades</li>
          <li>Deploying automated trading strategies (bots)</li>
          <li>Viewing AI-powered price predictions and sentiment analysis</li>
          <li>Scanning markets for trading opportunities</li>
        </ul>
        <p>
          SignalDeck is a software tool. We do not provide investment advice, manage your money, or guarantee any
          trading outcomes.
        </p>
      </section>

      {/* 3. Eligibility */}
      <section className={styles.section} id="eligibility">
        <h2 className={styles.sectionTitle}>3. Eligibility</h2>
        <p>
          You must be at least 18 years of age to use SignalDeck. By using the Service, you represent and warrant that
          you meet this age requirement and that you comply with all applicable federal, state, and local securities
          laws and regulations in your jurisdiction.
        </p>
      </section>

      {/* 4. Account Registration */}
      <section className={styles.section} id="registration">
        <h2 className={styles.sectionTitle}>4. Account Registration</h2>
        <p>
          To use SignalDeck, you must create an account and provide accurate, complete, and current information. You are
          responsible for:
        </p>
        <ul>
          <li>Maintaining the confidentiality of your login credentials</li>
          <li>All activities that occur under your account</li>
          <li>Promptly notifying us of any unauthorized access or security breach</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that contain inaccurate information or violate these
          terms.
        </p>
      </section>

      {/* 5. Third-Party Services */}
      <section className={styles.section} id="third-party">
        <h2 className={styles.sectionTitle}>5. Third-Party Services</h2>
        <p>
          SignalDeck integrates with Alpaca Markets Inc. for brokerage services. When you connect your brokerage
          account, you authorize SignalDeck to access your account data and execute trades on your behalf according to
          your instructions and configured strategies.
        </p>
        <p>
          You must agree to Alpaca's terms of service and customer agreements separately. SignalDeck is not a party to
          the agreement between you and your brokerage provider.
        </p>
        <div className={styles.callout}>
          <p>
            <strong>Important:</strong> SignalDeck is not a broker-dealer, investment advisor, or financial institution.
            We are a software platform that connects to third-party brokerage services at your direction.
          </p>
        </div>
      </section>

      {/* 6. Trading Risk Disclaimer */}
      <section className={styles.section} id="risk">
        <h2 className={styles.sectionTitle}>6. Trading Risk Disclaimer</h2>
        <div className={styles.dangerCallout}>
          <p>
            <strong>
              Trading stocks, options, and other securities involves substantial risk of loss and is not suitable for
              every investor.
            </strong>
          </p>
          <p>
            You could lose some or all of your invested capital. Past performance is not indicative of future results.
          </p>
        </div>
        <p>By using SignalDeck, you acknowledge and agree that:</p>
        <ul>
          <li>
            <strong>AI predictions and trading signals are NOT financial advice.</strong> They are analytical tools
            based on historical data, machine learning models, and market sentiment. They can be wrong.
          </li>
          <li>
            <strong>Automated trading bots may execute trades that result in significant losses.</strong> Algorithmic
            strategies carry additional risks including software bugs, connectivity issues, and rapidly changing market
            conditions.
          </li>
          <li>
            <strong>You are solely responsible for your trading decisions.</strong> Whether you act on signals,
            predictions, or bot recommendations, the decision to trade and the consequences of that trade are entirely
            yours.
          </li>
          <li>
            <strong>Options trading carries substantial risk</strong> and is not appropriate for all investors. You can
            lose more than your initial investment.
          </li>
          <li>
            <strong>Backtested or hypothetical performance does not guarantee future results.</strong> Historical
            simulations have inherent limitations and do not account for all market conditions.
          </li>
        </ul>
        <div className={styles.warningCallout}>
          <p>
            <strong>SignalDeck is not a registered investment advisor, broker-dealer, or financial planner.</strong> If
            you need personalized financial advice, consult a licensed professional.
          </p>
        </div>
        <p>
          For a comprehensive overview of trading risks, please review our{' '}
          <Link to="/risk-disclosure">Risk Disclosure</Link> page.
        </p>
      </section>

      {/* 7. Intellectual Property */}
      <section className={styles.section} id="ip">
        <h2 className={styles.sectionTitle}>7. Intellectual Property</h2>
        <p>
          All content, software, code, design, logos, trademarks, and branding associated with SignalDeck are the
          exclusive property of SignalDeck and are protected by applicable intellectual property laws. You may not copy,
          modify, distribute, or reverse-engineer any part of the Service without prior written consent.
        </p>
      </section>

      {/* 8. Limitation of Liability */}
      <section className={styles.section} id="liability">
        <h2 className={styles.sectionTitle}>8. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, SignalDeck and its officers, directors, employees, and affiliates
          shall not be liable for:
        </p>
        <ul>
          <li>Any trading losses or lost profits arising from use of the Service</li>
          <li>Data delays, inaccuracies, or interruptions in market data feeds</li>
          <li>API outages or failures of third-party brokerage services</li>
          <li>Unauthorized access to your account due to your failure to safeguard credentials</li>
          <li>Any indirect, incidental, special, consequential, or punitive damages</li>
        </ul>
        <p>
          In no event shall SignalDeck's total aggregate liability exceed the amount of fees you have paid to SignalDeck
          in the twelve (12) months preceding the claim.
        </p>
      </section>

      {/* 9. Indemnification */}
      <section className={styles.section} id="indemnification">
        <h2 className={styles.sectionTitle}>9. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless SignalDeck and its officers, directors, employees, and
          affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including
          reasonable attorneys' fees) arising from your use of the Service, your trading activities, or your violation
          of these Terms.
        </p>
      </section>

      {/* 10. Termination */}
      <section className={styles.section} id="termination">
        <h2 className={styles.sectionTitle}>10. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your account at any time for violations of these Terms,
          fraudulent activity, or any other reason at our sole discretion. You may delete your account at any time
          through your account settings or by contacting us.
        </p>
        <p>
          Upon termination, your right to use the Service ceases immediately. We will delete your stored data in
          accordance with our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </section>

      {/* 11. Modifications */}
      <section className={styles.section} id="modifications">
        <h2 className={styles.sectionTitle}>11. Modifications to Terms</h2>
        <p>
          We may update these Terms of Service from time to time. When we make material changes, we will notify you via
          email or in-app notification. Your continued use of the Service after such changes constitutes acceptance of
          the updated terms.
        </p>
      </section>

      {/* 12. Governing Law */}
      <section className={styles.section} id="governing-law">
        <h2 className={styles.sectionTitle}>12. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of Indiana, without
          regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the
          state or federal courts located in Indiana.
        </p>
      </section>

      {/* 13. Contact */}
      <section className={styles.section} id="contact">
        <h2 className={styles.sectionTitle}>13. Contact</h2>
        <p>
          If you have questions about these Terms of Service, please contact us at{' '}
          <a href="mailto:support@signaldeck.app" className={styles.contactEmail}>
            support@signaldeck.app
          </a>
          .
        </p>
      </section>

      {/* Back to top */}
      <div className={styles.backToTop}>
        <a href="#top" className={styles.backToTopLink}>
          Back to top
        </a>
      </div>
    </div>
  );
};

export default TermsOfService;
